// src/app/jobs.jsx
import React, { useEffect, useState } from 'react';
import { View, Text, FlatList, StyleSheet, TouchableOpacity } from 'react-native';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { IoCalendar, IoNavigate, IoAdd } from 'react-icons/io5';
import API_BASE_URL from '../apiConfig.js';

// token helper
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const navigate = useNavigate();

  // Load user permissions on component mount
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          // Check if user has job permissions
          if (parsedPermissions?.job_perm === 'none') {
            alert("You don't have permission to view jobs. Please contact your account administrator.");
            navigate('/'); // Redirect to home page
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  // Check if user has job write permissions
  const hasJobWritePermission = () => {
    return userPermissions?.job_perm === 'write';
  };

  // Check if user can see prices
  const canSeePrices = () => {
    return userPermissions?.price_perm === 'visible';
  };

  const loadJobs = async (nextPage = 1) => {
    if (isLoading || !hasMore) return;
    try {
      setIsLoading(true);
      const token = getToken();
      const { data } = await axios.get(
        `${API_BASE_URL}/jobs/all?page=${nextPage}&page_size=25`,
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      const newJobs = Array.isArray(data) ? data : [];
      setJobs((prev) => [...prev, ...newJobs]);
      setPage(nextPage);
      if (newJobs.length < 25) setHasMore(false);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      alert('Failed to load jobs. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadJobs(1); /* eslint-disable-line */ }, []);

  const formatDateTime = (selectedDate, selectedTime) => {
    if (!selectedDate || !selectedTime) return 'Invalid Date/Time';
    const d = new Date(`${selectedDate}T${selectedTime}Z`);
    const ds = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const ts = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return `${ds} ${ts}`;
  };

  const handleCompletePress = (job) => {
    setSelectedJob(job);
    setCompletionOpen(true);
  };

  const handleCompletionConfirm = async () => {
    if (!selectedJob) return;
    try {
      const token = getToken();
      await axios.put(
        `${API_BASE_URL}/jobs/edit/${selectedJob.id}`,
        { ...selectedJob, completed: 'yes' },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      setCompletionOpen(false);
      
      // Update the job in the local state immediately
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === selectedJob.id 
            ? { ...job, completed: 'yes' }
            : job
        )
      );
      
      // Check permissions before showing quote/invoice creation modals
      if (selectedJob.visitType === 'quote') {
        // Only show quote modal if user has quote write permission
        if (userPermissions?.quote_perm === 'write') {
          setQuoteOpen(true);
        }
      } else {
        // Only show invoice modal if user has invoice write permission
        if (userPermissions?.invoice_perm === 'write') {
          setInvoiceOpen(true);
        }
      }
    } catch (err) {
      console.error('Error updating job completion:', err);
      window.alert('Failed to update job completion. Please try again later.');
    }
  };

  const handleQuoteConfirm = () => {
    if (!selectedJob) return;
    setQuoteOpen(false);
    const q = new URLSearchParams({
      id: String(selectedJob.id),
      customerId: String(selectedJob.customerId ?? ''),
      customerName: selectedJob.customerName ?? '',
      title: selectedJob.title ?? '',
      description: selectedJob.description ?? '',
      price: String(selectedJob.price ?? ''),
    }).toString();
    navigate(`/create-quote?${q}`);
  };

  const handleQuoteCancel = async () => {
    setQuoteOpen(false);
    // No need to refresh jobs list since we already updated it
  };

  const handleInvoiceConfirm = () => {
    if (!selectedJob) return;
    setInvoiceOpen(false);
    const q = new URLSearchParams({
      customerName: selectedJob.customerName ?? '',
      title: selectedJob.title ?? '',
      description: selectedJob.description ?? '',
      price: String(selectedJob.price ?? ''),
      selectedDate: selectedJob.selectedDate ?? '',
      selectedTime: selectedJob.selectedTime ?? '',
      jobId: String(selectedJob.id),
    }).toString();
    navigate(`/create-invoice?${q}`);
  };

  const handleInvoiceCancel = async () => {
    setInvoiceOpen(false);
    // No need to refresh jobs list since we already updated it
  };

  const undoComplete = async (job) => {
    try {
      const token = getToken();
      await axios.put(
        `${API_BASE_URL}/jobs/edit/${job.id}`,
        { ...job, completed: 'no' },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      
      // Update the job in the local state immediately
      setJobs(prevJobs => 
        prevJobs.map(existingJob => 
          existingJob.id === job.id 
            ? { ...existingJob, completed: 'no' }
            : existingJob
        )
      );
    } catch (e) {
      console.error('Error undoing job completion', e);
      window.alert('Failed to undo job completion. Please try again later.');
    }
  };

  const renderItem = ({ item }) => {
    const when = formatDateTime(item.selectedDate, item.selectedTime);
    return (
      <TouchableOpacity
        style={styles.card}
        onPress={() => navigate(`/job-details?id=${encodeURIComponent(item.id)}`)}
      >
        <Text
          style={[
            styles.topLeftLabel,
            {
              backgroundColor: item.visitType === 'job' ? 'green' : 'lightblue',
              color: item.visitType === 'job' ? 'white' : 'black',
            },
          ]}
        >
          {item.visitType === 'job' ? 'Job' : 'Quote'}
        </Text>
        <View style={[styles.cardHeader, { justifyContent: 'space-between', alignItems: 'flex-start' }]}>
          <Text style={[styles.jobTitle, { marginTop: 30 }]}>{item.title}</Text>
          <View style={styles.timeContainer}>
            <IoCalendar size={16} />
            <Text style={styles.timeText}>{when}</Text>
          </View>
        </View>
        <Text style={styles.jobDetail}>{`Description: ${item.description}`}</Text>
        <View style={styles.addressContainer}>
          <Text style={styles.jobDetail}>{`Address: ${item.address}`}</Text>
          <TouchableOpacity
            style={styles.navigateButton}
            onPress={() => {
              const address = `${item.address}, ${item.city}, ${item.state}, ${item.zip}`;
              const url = `https://maps.google.com/?q=${encodeURIComponent(address)}`;
              window.open(url, '_self');
            }}
          >
            <View style={styles.iconTextContainer}>
              <IoNavigate size={16} />
              <Text style={styles.navigateText}>Map</Text>
            </View>
          </TouchableOpacity>
        </View>
        <Text style={styles.jobDetail}>{`City: ${item.city}`}</Text>
        <Text style={styles.jobDetail}>{`State: ${item.state}`}</Text>
        <Text style={styles.jobDetail}>{`Zip: ${item.zip}`}</Text>
        {canSeePrices() && <Text style={styles.jobDetail}>{`Price: $${item.price}`}</Text>}

        {item.completed === 'no' ? (
          <TouchableOpacity
            style={[
              styles.completeButton,
              !hasJobWritePermission() && styles.disabledButton
            ]}
            onPress={() => hasJobWritePermission() && handleCompletePress(item)}
            disabled={!hasJobWritePermission()}
          >
            <Text style={styles.completeButtonText}>
              {hasJobWritePermission() ? '✔ Complete' : 'Incomplete'}
            </Text>
          </TouchableOpacity>
        ) : (
          <View style={styles.completedContainer}>
            <Text style={styles.completedText}>✔ Completed</Text>
            {hasJobWritePermission() && (
              <TouchableOpacity onPress={() => undoComplete(item)}>
                <Text style={styles.undoText}>Undo</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <Text style={styles.header}>All Jobs</Text>
      {jobs.length === 0 && !isLoading ? (
        <Text style={styles.noJobsText}>There are no jobs to display.</Text>
      ) : (
        <FlatList
          data={jobs}
          keyExtractor={(item, index) => String(item?.id ?? index)}
          renderItem={renderItem}
          ListFooterComponent={
            hasMore ? (
              <TouchableOpacity
                style={styles.loadMoreButton}
                onPress={() => loadJobs(page + 1)}
                disabled={isLoading}
              >
                <Text style={styles.loadMoreText}>{isLoading ? 'Loading...' : 'Load More'}</Text>
              </TouchableOpacity>
            ) : null
          }
        />
      )}

      {/* Completion Modal */}
      {completionOpen && (
        <View style={styles.overlay}>
          <View style={styles.overlayCard}>
            <Text style={styles.modalTitle}>Complete Job</Text>
            <Text style={styles.modalMessage}>Has this visit been completed?</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={handleCompletionConfirm}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalButton} onPress={() => setCompletionOpen(false)}>
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Invoice Modal */}
      {invoiceOpen && (
        <View style={styles.overlay}>
          <View style={styles.overlayCard}>
            <Text style={styles.modalTitle}>Create Invoice</Text>
            <Text style={styles.modalMessage}>Would you like to create an invoice now?</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={handleInvoiceConfirm}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalButton} onPress={handleInvoiceCancel}>
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Quote Modal */}
      {quoteOpen && (
        <View style={styles.overlay}>
          <View style={styles.overlayCard}>
            <Text style={styles.modalTitle}>Create Quote</Text>
            <Text style={styles.modalMessage}>Would you like to create a quote to send to the customer?</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={handleQuoteConfirm}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalButton} onPress={handleQuoteCancel}>
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      <TouchableOpacity style={styles.fab} onPress={() => navigate('/new-job')}>
        <IoAdd size={28} color="#fff" />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 15, paddingBottom: 50, backgroundColor: '#f9f9f9' },
  header: {
    fontSize: 24, fontWeight: 'bold', marginBottom: 15, textAlign: 'center',
    textDecorationLine: 'underline', color: '#333',
  },
  card: {
    backgroundColor: '#fff', padding: 15, borderRadius: 8, marginBottom: 10,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.2, shadowRadius: 2, elevation: 2,
  },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  jobTitle: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  timeContainer: { flexDirection: 'row', alignItems: 'center' },
  timeText: { fontSize: 14, color: '#666', marginLeft: 5 },
  jobDetail: { fontSize: 14, color: '#666' },
  noJobsText: { textAlign: 'center', color: '#999', fontSize: 16, marginTop: 20 },
  loadMoreButton: { padding: 10, backgroundColor: '#007AFF', borderRadius: 5, alignItems: 'center', marginVertical: 10 },
  loadMoreText: { color: '#fff', fontSize: 16 },
  fab: {
    position: 'absolute', bottom: 90, right: 15, backgroundColor: '#007BFF',
    width: 50, height: 50, borderRadius: 25, justifyContent: 'center', alignItems: 'center'
  },
  topLeftLabel: {
    position: 'absolute',
    top: 10,
    left: 10,
    backgroundColor: 'lightblue',
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 2,
    paddingBottom: 2,
    borderRadius: 4,
    fontWeight: 'bold',
    color: 'black',
  },
  addressContainer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 0 },
  navigateButton: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'transparent', paddingVertical: 4, paddingHorizontal: 8, borderRadius: 4, marginLeft: 10 },
  iconTextContainer: { flexDirection: 'row', alignItems: 'center' },
  navigateText: { color: '#007AFF', fontWeight: 'bold', marginLeft: 4 },
  completeButton: {
    marginTop: -30,
    backgroundColor: '#28a745',
    padding: 8,
    borderRadius: 4,
    alignSelf: 'flex-end',
  },
  completeButtonText: { color: '#fff', fontWeight: 'bold', fontSize: 14 },
  disabledButton: { backgroundColor: '#ccc' },
  completedContainer: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    justifyContent: 'flex-end',
    marginTop: 10,
    gap: 10,
  },
  completedText: { color: '#28a745', fontSize: 14, fontWeight: 'bold' },
  undoText: { 
    color: '#007AFF', 
    fontSize: 14, 
    fontWeight: 'bold',
    textDecorationLine: 'underline',
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  overlayCard: {
    backgroundColor: '#fff',
    padding: 20,
    borderRadius: 10,
    width: '80%',
    maxWidth: 400,
    alignItems: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
  modalTitle: { fontSize: 20, fontWeight: 'bold', marginBottom: 10 },
  modalMessage: { fontSize: 16, color: '#333', textAlign: 'center', marginBottom: 20 },
  modalButtons: { flexDirection: 'row', justifyContent: 'space-between', width: '100%' },
  modalButton: {
    flex: 1,
    padding: 10,
    backgroundColor: '#007AFF',
    borderRadius: 5,
    alignItems: 'center',
    marginHorizontal: 5,
  },
  modalButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
});
